import { useEffect, useState } from "react";
import { api, fmtMoney, dimensionSummary } from "@/lib/api";
import { Plus, MagnifyingGlass, Cylinder, Square, Rectangle, FlowArrow } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const EMPTY = {
  name: "", description: "", shape: "none",
  dimensions: {}, unit: "pcs",
  default_cost: 0, default_cost_currency: "CNY",
  default_sell: 0, default_sell_currency: "PKR",
  weight: "", code: "",
  supplier_id: "", tags: [], notes: "", status: "active",
};

const SHAPE_FIELDS = {
  square: [["side", "Side (mm)"], ["height", "Height (mm)"]],
  rectangle: [["length", "Length (mm)"], ["width", "Width (mm)"], ["height", "Height (mm)"]],
  cylinder: [["diameter", "Diameter (mm)"], ["length", "Length (mm)"]],
  pipe: [["outer_diameter", "Outer Ø (mm)"], ["wall_thickness", "Wall (mm)"], ["length", "Length (mm)"]],
};

const SHAPE_ICONS = {
  none: null, square: Square, rectangle: Rectangle, cylinder: Cylinder, pipe: FlowArrow,
};

export default function Items() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const canManage = hasRole("admin", "sales", "operations");

  const startEdit = (it) => {
    setEditing(it);
    setForm({ ...EMPTY, ...it, dimensions: { ...(it.dimensions || {}) } });
    setOpen(true);
  };
  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const updateDim = (key, value) => {
    setForm({ ...form, dimensions: { ...form.dimensions, [key]: Number(value) || 0 } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_cost: Number(form.default_cost) || 0,
        default_sell: Number(form.default_sell) || 0,
        weight: form.weight === "" || form.weight == null ? null : Number(form.weight),
        code: form.code?.trim() || null,
        supplier_id: form.supplier_id || null,
      };
      if (editing) {
        await api.put(`/items/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/items", payload);
        toast.success("Item created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const filtered = items.filter(
    (it) =>
      it.name?.toLowerCase().includes(q.toLowerCase()) ||
      it.code?.toLowerCase().includes(q.toLowerCase()) ||
      it.description?.toLowerCase().includes(q.toLowerCase()),
  );

  const dimFields = SHAPE_FIELDS[form.shape] || [];

  return (
    <div className="space-y-5" data-testid="items-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Catalog</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Items</h1>
          <p className="text-xs text-zinc-500 mt-1">Define your reusable item catalog. These appear as suggestions in order line items.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="item-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="h-9 pl-8 w-72 rounded-sm" />
          </div>
          {canManage && (
            <Button onClick={startNew} className="h-9 rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="add-item-btn">
              <Plus size={14} className="mr-1" /> New Item
            </Button>
          )}
        </div>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Code</th>
              <th className="label-mini px-4 py-3">Name</th>
              <th className="label-mini px-4 py-3">Shape / Dimensions</th>
              <th className="label-mini px-4 py-3">Unit</th>
              <th className="label-mini px-4 py-3 text-right">Default Cost</th>
              <th className="label-mini px-4 py-3 text-right">Default Sell</th>
              <th className="label-mini px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const Icon = SHAPE_ICONS[it.shape];
              return (
                <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`item-row-${it.code}`}>
                  <td className="px-4 py-3 font-mono text-xs">{it.code}</td>
                  <td className="px-4 py-3 font-medium">
                    {it.name}
                    {it.description && <div className="text-[11px] text-zinc-500">{it.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      {Icon && <Icon size={12} />}
                      <span className="capitalize">{it.shape}</span>
                      {dimensionSummary(it) && <span className="text-zinc-500 font-mono">· {dimensionSummary(it)}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{it.unit}</td>
                  <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(it.default_cost, it.default_cost_currency)}</td>
                  <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(it.default_sell, it.default_sell_currency)}</td>
                  <td className="px-4 py-3 text-right">
                    {canManage && (
                      <button onClick={() => startEdit(it)} className="text-xs font-semibold underline" data-testid={`edit-item-${it.code}`}>Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500 text-sm">No items yet. Click "New Item" to start your catalog.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="item-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label="Name" required>
                <Input data-testid="item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Description">
                <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-16" />
              </Field>
            </div>
            <Field label="Shape">
              <Select value={form.shape} onValueChange={(v) => setForm({ ...form, shape: v, dimensions: {} })}>
                <SelectTrigger className="rounded-sm h-9" data-testid="item-shape"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="square">Square</SelectItem>
                  <SelectItem value="rectangle">Rectangle</SelectItem>
                  <SelectItem value="cylinder">Cylinder</SelectItem>
                  <SelectItem value="pipe">Pipe</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Unit">
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-sm h-9" />
            </Field>
            {dimFields.length > 0 && (
              <div className="col-span-2 grid grid-cols-3 gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                {dimFields.map(([k, label]) => (
                  <Field key={k} label={label}>
                    <Input
                      type="number"
                      value={form.dimensions?.[k] ?? ""}
                      onChange={(e) => updateDim(k, e.target.value)}
                      className="rounded-sm h-9 font-mono"
                      data-testid={`item-dim-${k}`}
                    />
                  </Field>
                ))}
              </div>
            )}
            <Field label="Default Cost">
              <div className="flex gap-1">
                <Input type="number" value={form.default_cost} onChange={(e) => setForm({ ...form, default_cost: e.target.value })} className="rounded-sm h-9 font-mono" />
                <Select value={form.default_cost_currency} onValueChange={(v) => setForm({ ...form, default_cost_currency: v })}>
                  <SelectTrigger className="rounded-sm h-9 w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PKR">PKR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="CNY">CNY</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Field>
            <Field label="Default Selling">
              <div className="flex gap-1">
                <Input type="number" value={form.default_sell} onChange={(e) => setForm({ ...form, default_sell: e.target.value })} className="rounded-sm h-9 font-mono" />
                <Select value={form.default_sell_currency} onValueChange={(v) => setForm({ ...form, default_sell_currency: v })}>
                  <SelectTrigger className="rounded-sm h-9 w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PKR">PKR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="CNY">CNY</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Field>
            <Field label="Weight (kg, optional)">
              <Input type="number" min="0" step="any" data-testid="item-weight" value={form.weight ?? ""} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="rounded-sm h-9 font-mono" />
            </Field>
            <Field label="Item Code (optional)">
              <Input data-testid="item-code" value={form.code || ""} placeholder="Auto-generated if blank" onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm h-9 font-mono" />
            </Field>
            <Field label="Default Supplier">
              <Select value={form.supplier_id || ""} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-item-btn" onClick={save} disabled={saving || !form.name} className="rounded-sm bg-black text-white hover:bg-zinc-800">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
